import { CourseLite } from "../types/course.response";

export function sortCoursesById<T extends CourseLite>(courses: T[]): T[] {
    return [...courses].sort((a, b) => {
        const subjectCmp = a.courseInfo.subject.localeCompare(b.courseInfo.subject);
        if (subjectCmp !== 0) return subjectCmp;
        return parseInt(a.courseInfo.number) - parseInt(b.courseInfo.number);
    });
}

const randomKeys = new Map<string, number>();

export function getRandomKey(id: string): number {
    if (!randomKeys.has(id)) {
        randomKeys.set(id, Math.random());
    }
    return randomKeys.get(id)!;
}

interface SortCoursesOptions {
    includeCompleted?: boolean;
    prioritizeLevels?: number[];
    prioritizeUG?: boolean;
}

export function sortCourses<T extends CourseLite>(courses: T[], options?: SortCoursesOptions): T[] {
    const { includeCompleted = false, prioritizeLevels, prioritizeUG = false } = options ?? {};

    const eligibleCourses = includeCompleted
        ? [...courses]
        : courses.filter(c => c.userState.status !== 'COMPLETED');

    if (eligibleCourses.length === 0) {
        return [];
    }

    const priorityLevelSet = prioritizeLevels ? new Set(prioritizeLevels) : null;

    const sortedCourses = eligibleCourses.sort((a, b) => {
        if (includeCompleted) {
            if (a.userState.status === 'COMPLETED' || b.userState.status === 'COMPLETED') {
                if (a.userState.status === b.userState.status) return 0;
                return a.userState.status === 'COMPLETED' ? -1 : 1;
            }
        }

        if (a.userState.status === 'IN_PROGRESS' || b.userState.status === 'IN_PROGRESS') {
            if (a.userState.status === b.userState.status) return 0;
            return a.userState.status === 'IN_PROGRESS' ? -1 : 1;
        }

        const aIsAvailable = a.courseState.isSemesterAvailable && a.courseState.isLocationAvailable;
        const bIsAvailable = b.courseState.isSemesterAvailable && b.courseState.isLocationAvailable;

        // 用户已排课的状态优先于普通可选课程,PLANNED 高于 SAVED
        const scheduledRank: Record<string, number> = { 'PLANNED': 2, 'SAVED': 1 };
        const aScheduled = scheduledRank[a.userState.status] ?? 0;
        const bScheduled = scheduledRank[b.userState.status] ?? 0;
        if (aScheduled !== bScheduled) {
            return bScheduled - aScheduled;
        }

        if ((priorityLevelSet || prioritizeUG) && aScheduled === 0) {
            const aIsPriority = (
                (priorityLevelSet ? priorityLevelSet.has(a.courseInfo.level) : false) ||
                (prioritizeUG ? a.courseInfo.acadCareer === 'UG' : false)
            ) && aIsAvailable;
            const bIsPriority = (
                (priorityLevelSet ? priorityLevelSet.has(b.courseInfo.level) : false) ||
                (prioritizeUG ? b.courseInfo.acadCareer === 'UG' : false)
            ) && bIsAvailable;
            if (aIsPriority !== bIsPriority) {
                return aIsPriority ? -1 : 1;
            }
        }

        if (aIsAvailable !== bIsAvailable) {
            return aIsAvailable ? -1 : 1;
        }

        const statusPriority = { 'PLANNED': 3, 'SAVED': 2, 'NOT_ON_SCHEDULE': 1 };
        const aPriority = statusPriority[a.userState.status as keyof typeof statusPriority] || 0;
        const bPriority = statusPriority[b.userState.status as keyof typeof statusPriority] || 0;
        if (aPriority !== bPriority) {
            return bPriority - aPriority;
        }

        return getRandomKey(a.id) - getRandomKey(b.id);
    });

    return sortedCourses;
}
